import React, { useState, useEffect } from 'react';
import { db } from '../../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export function AdmLogoManager() {
  const [leftWallLogoUrl, setLeftWallLogoUrl] = useState<string | null>(null);
  const [backWallLogoUrl, setBackWallLogoUrl] = useState<string | null>(null);

  // Slot 1 (Left Logo)
  const [leftScale, setLeftScale] = useState<number>(380);
  const [leftOffsetY, setLeftOffsetY] = useState<number>(250);
  const [leftOffsetX, setLeftOffsetX] = useState<number>(0);
  const [leftOffsetZ, setLeftOffsetZ] = useState<number>(0);
  const [leftRotationY, setLeftRotationY] = useState<number>(90); // 90° = flush to side wall

  // Slot 2 (Right/Back Logo)
  const [backScale, setBackScale] = useState<number>(380);
  const [backOffsetY, setBackOffsetY] = useState<number>(250);
  const [backOffsetX, setBackOffsetX] = useState<number>(0);
  const [backOffsetZ, setBackOffsetZ] = useState<number>(0);
  const [backRotationY, setBackRotationY] = useState<number>(0);

  const [uploadingLeft, setUploadingLeft] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogos = async () => {
      try {
        const docRef = doc(db, 'config', 'terminal_assets');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setLeftWallLogoUrl(data.leftWallLogoUrl || null);
          setBackWallLogoUrl(data.backWallLogoUrl || null);
          if (typeof data.leftScale === 'number') setLeftScale(data.leftScale);
          if (typeof data.leftOffsetY === 'number') setLeftOffsetY(data.leftOffsetY);
          if (typeof data.leftOffsetX === 'number') setLeftOffsetX(data.leftOffsetX);
          if (typeof data.leftOffsetZ === 'number') setLeftOffsetZ(data.leftOffsetZ);
          if (typeof data.leftRotationY === 'number') setLeftRotationY(data.leftRotationY);
          if (typeof data.backScale === 'number') setBackScale(data.backScale);
          if (typeof data.backOffsetY === 'number') setBackOffsetY(data.backOffsetY);
          if (typeof data.backOffsetX === 'number') setBackOffsetX(data.backOffsetX);
          if (typeof data.backOffsetZ === 'number') setBackOffsetZ(data.backOffsetZ);
          if (typeof data.backRotationY === 'number') setBackRotationY(data.backRotationY);
        }
      } catch (err) {
        console.error('Error fetching logos:', err);
      }
    };
    fetchLogos();
  }, []);

  const saveSetting = async (key: string, value: number) => {
    try {
      const docRef = doc(db, 'config', 'terminal_assets');
      await setDoc(docRef, {
        [key]: value
      }, { merge: true });
    } catch (e) {
      console.error('Error saving setting:', e);
    }
  };

  const handleSaveAllCloud = async () => {
    try {
      setStatusMsg('Salvando na nuvem...');
      const docRef = doc(db, 'config', 'terminal_assets');
      await setDoc(docRef, {
        leftWallLogoUrl,
        leftScale,
        leftOffsetY,
        leftOffsetX,
        leftOffsetZ,
        leftRotationY,
        backWallLogoUrl,
        backScale,
        backOffsetY,
        backOffsetX,
        backOffsetZ,
        backRotationY,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      setStatusMsg('Configurações salvas na nuvem com sucesso!');
      setTimeout(() => setStatusMsg(null), 3500);
    } catch (e) {
      console.error('Erro ao salvar na nuvem:', e);
      setStatusMsg('Erro ao salvar na nuvem');
    }
  };

  const processAndCompressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const maxDim = 800; // max dimension for crisp high-res rendering
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png'));
          } else {
            resolve(e.target?.result as string);
          }
        };
        img.onerror = () => reject(new Error('Falha ao processar a imagem.'));
        img.src = e.target?.result as string;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>, position: 'left' | 'back') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.includes('png') && !file.type.includes('image')) {
      alert('Por favor, selecione uma imagem PNG válida com fundo transparente.');
      return;
    }

    if (position === 'left') setUploadingLeft(true);
    if (position === 'back') setUploadingBack(true);
    setStatusMsg('Processando imagem...');

    try {
      const dataUrl = await processAndCompressImage(file);
      const docRef = doc(db, 'config', 'terminal_assets');

      if (position === 'left') {
        await setDoc(docRef, {
          leftWallLogoUrl: dataUrl,
          leftScale: leftScale,
          leftOffsetY: leftOffsetY,
          leftOffsetX: leftOffsetX,
          leftOffsetZ: leftOffsetZ
        }, { merge: true });
        setLeftWallLogoUrl(dataUrl);
      } else {
        await setDoc(docRef, {
          backWallLogoUrl: dataUrl,
          backScale: backScale,
          backOffsetY: backOffsetY,
          backOffsetX: backOffsetX,
          backOffsetZ: backOffsetZ
        }, { merge: true });
        setBackWallLogoUrl(dataUrl);
      }
      setStatusMsg('Logo salva com sucesso!');
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err) {
      console.error("Error uploading logo:", err);
      alert("Erro ao salvar a logo. Verifique a imagem e tente novamente.");
    } finally {
      if (position === 'left') setUploadingLeft(false);
      if (position === 'back') setUploadingBack(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (position: 'left' | 'back') => {
    const confirmDelete = window.confirm("Tem certeza que deseja remover esta logo do terminal 3D?");
    if (!confirmDelete) return;

    try {
      const docRef = doc(db, 'config', 'terminal_assets');
      if (position === 'left') {
        await setDoc(docRef, {
          leftWallLogoUrl: null
        }, { merge: true });
        setLeftWallLogoUrl(null);
      } else {
        await setDoc(docRef, {
          backWallLogoUrl: null
        }, { merge: true });
        setBackWallLogoUrl(null);
      }
      setStatusMsg('Logo removida com sucesso!');
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err) {
      console.error("Error deleting logo:", err);
      alert("Erro ao excluir a logo.");
    }
  };

  return (
    <div className="space-y-4 p-2 bg-[#030509]/60 border border-white/5 rounded-lg">
      <div className="flex items-center justify-between border-b border-white/10 pb-2">
        <h2 className="text-[#00ffa3] text-[10px] font-mono font-bold tracking-widest uppercase flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#00ffa3] animate-pulse" />
          GERENCIADOR & DIMENSIONADOR DE LOGOS 3D
        </h2>
        <div className="flex items-center gap-2">
          {statusMsg && (
            <span className="text-[9px] font-mono text-[#00ffa3] bg-[#00ffa3]/10 px-2 py-0.5 rounded border border-[#00ffa3]/20 animate-pulse">
              {statusMsg}
            </span>
          )}
          <button
            onClick={handleSaveAllCloud}
            className="text-[9px] font-mono font-bold text-black bg-[#00ffa3] hover:bg-[#00e08f] px-3 py-1 rounded shadow-[0_0_15px_rgba(0,255,163,0.3)] transition-all cursor-pointer flex items-center gap-1"
          >
            ☁️ SALVAR TUDO NA NUVEM
          </button>
        </div>
      </div>

      {/* SLOT 1 */}
      <div className="bg-[#05070e] p-3 border border-white/5 rounded-md space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-[9px] font-mono font-bold text-white/80 tracking-wider">
            SLOT 1: LOGO ONG (ESQUERDA)
          </span>
          <span className="text-[8px] font-mono text-white/40">Ajustes em Tempo Real</span>
        </div>

        {leftWallLogoUrl ? (
          <div className="flex flex-col gap-3 bg-black/40 p-3 border border-white/10 rounded">
            <div className="max-h-24 flex items-center justify-center overflow-hidden p-2 bg-checkerboard rounded">
              <img src={leftWallLogoUrl} alt="Logo Esquerda" className="max-h-20 object-contain drop-shadow" />
            </div>

            {/* CONTROLES DE DIMENSÃO E POSIÇÃO */}
            <div className="space-y-2 border-t border-white/10 pt-2 text-[9px] font-mono">
              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Tamanho / Escala:</span>
                  <span className="text-[#00ffa3] font-bold">{leftScale}px</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="1200"
                  step="10"
                  value={leftScale}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setLeftScale(val);
                    saveSetting('leftScale', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>

              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Altura (Elevação Y):</span>
                  <span className="text-[#00ffa3] font-bold">{leftOffsetY}px</span>
                </div>
                <input
                  type="range"
                  min="-200"
                  max="1000"
                  step="10"
                  value={leftOffsetY}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setLeftOffsetY(val);
                    saveSetting('leftOffsetY', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>

              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Posição Horizontal (Eixo X):</span>
                  <span className="text-[#00ffa3] font-bold">{leftOffsetX}px</span>
                </div>
                <input
                  type="range"
                  min="-800"
                  max="800"
                  step="10"
                  value={leftOffsetX}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setLeftOffsetX(val);
                    saveSetting('leftOffsetX', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>

              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Profundidade (Eixo Z):</span>
                  <span className="text-[#00ffa3] font-bold">{leftOffsetZ}px</span>
                </div>
                <input
                  type="range"
                  min="-800"
                  max="800"
                  step="10"
                  value={leftOffsetZ}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setLeftOffsetZ(val);
                    saveSetting('leftOffsetZ', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>

              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Rotação Y (Ângulo Parede Lateral - 90° Colada):</span>
                  <span className="text-[#00ffa3] font-bold">{leftRotationY}°</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="360"
                  step="1"
                  value={leftRotationY}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setLeftRotationY(val);
                    saveSetting('leftRotationY', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>
            </div>

            <div className="flex gap-2 w-full justify-end pt-1">
              <button
                onClick={() => handleDelete('left')}
                className="text-[9px] text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 px-3 py-1 border border-red-500/30 rounded font-mono transition-all cursor-pointer"
              >
                EXCLUIR LOGO E TROCAR
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <label className="flex flex-col items-center justify-center border border-dashed border-white/20 hover:border-[#00ffa3]/50 rounded-md p-4 cursor-pointer bg-white/5 hover:bg-[#00ffa3]/5 transition-all">
              <span className="text-[10px] text-white/70 font-mono font-medium">
                {uploadingLeft ? 'Processando e salvando...' : 'Clique para selecionar a imagem PNG'}
              </span>
              <span className="text-[8px] text-white/40 font-mono mt-1">Recomendado: PNG com fundo transparente</span>
              <input
                type="file"
                accept="image/png,image/*"
                onChange={(e) => handleUpload(e, 'left')}
                className="hidden"
                disabled={uploadingLeft}
              />
            </label>
          </div>
        )}
      </div>

      {/* SLOT 2 */}
      <div className="bg-[#05070e] p-3 border border-white/5 rounded-md space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-[9px] font-mono font-bold text-white/80 tracking-wider">
            SLOT 2: PALAVRA FLOWAES (DIREITA)
          </span>
          <span className="text-[8px] font-mono text-white/40">Ajustes em Tempo Real</span>
        </div>

        {backWallLogoUrl ? (
          <div className="flex flex-col gap-3 bg-black/40 p-3 border border-white/10 rounded">
            <div className="max-h-24 flex items-center justify-center overflow-hidden p-2 bg-checkerboard rounded">
              <img src={backWallLogoUrl} alt="Logo Direita" className="max-h-20 object-contain drop-shadow" />
            </div>

            {/* CONTROLES DE DIMENSÃO E POSIÇÃO */}
            <div className="space-y-2 border-t border-white/10 pt-2 text-[9px] font-mono">
              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Tamanho / Escala:</span>
                  <span className="text-[#00ffa3] font-bold">{backScale}px</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="1200"
                  step="10"
                  value={backScale}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setBackScale(val);
                    saveSetting('backScale', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>

              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Altura (Elevação Y):</span>
                  <span className="text-[#00ffa3] font-bold">{backOffsetY}px</span>
                </div>
                <input
                  type="range"
                  min="-200"
                  max="1000"
                  step="10"
                  value={backOffsetY}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setBackOffsetY(val);
                    saveSetting('backOffsetY', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>

              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Posição Horizontal (Eixo X):</span>
                  <span className="text-[#00ffa3] font-bold">{backOffsetX}px</span>
                </div>
                <input
                  type="range"
                  min="-800"
                  max="800"
                  step="10"
                  value={backOffsetX}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setBackOffsetX(val);
                    saveSetting('backOffsetX', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>

              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Profundidade (Eixo Z):</span>
                  <span className="text-[#00ffa3] font-bold">{backOffsetZ}px</span>
                </div>
                <input
                  type="range"
                  min="-800"
                  max="800"
                  step="10"
                  value={backOffsetZ}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setBackOffsetZ(val);
                    saveSetting('backOffsetZ', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>

              <div>
                <div className="flex justify-between text-white/70 mb-1">
                  <span>Rotação Y (Ângulo Parede Traseira - 0° Plana):</span>
                  <span className="text-[#00ffa3] font-bold">{backRotationY}°</span>
                </div>
                <input
                  type="range"
                  min="-180"
                  max="180"
                  step="1"
                  value={backRotationY}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setBackRotationY(val);
                    saveSetting('backRotationY', val);
                  }}
                  className="w-full h-1 bg-white/10 rounded appearance-none cursor-pointer accent-[#00ffa3]"
                />
              </div>
            </div>

            <div className="flex gap-2 w-full justify-end pt-1">
              <button
                onClick={() => handleDelete('back')}
                className="text-[9px] text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 px-3 py-1 border border-red-500/30 rounded font-mono transition-all cursor-pointer"
              >
                EXCLUIR LOGO E TROCAR
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <label className="flex flex-col items-center justify-center border border-dashed border-white/20 hover:border-[#00ffa3]/50 rounded-md p-4 cursor-pointer bg-white/5 hover:bg-[#00ffa3]/5 transition-all">
              <span className="text-[10px] text-white/70 font-mono font-medium">
                {uploadingBack ? 'Processando e salvando...' : 'Clique para selecionar a imagem PNG'}
              </span>
              <span className="text-[8px] text-white/40 font-mono mt-1">Recomendado: PNG com fundo transparente</span>
              <input
                type="file"
                accept="image/png,image/*"
                onChange={(e) => handleUpload(e, 'back')}
                className="hidden"
                disabled={uploadingBack}
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}
